import { useContext, useMemo, useRef, useCallback } from 'react'
import * as React from 'react'
import type { TrackingProps } from 'contexts/trackingContext'
import TrackingContext from 'contexts/trackingContext'
import type { ArrowDirection } from 'LuxKit/Carousel/CardCarousel'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import debounce from 'lodash.debounce'
import { useInView } from 'react-intersection-observer'
import { getLogicalScrollLeft } from 'LuxKit/Carousel/utils'

interface Props extends React.ComponentProps<typeof CardCarousel> {}

function TrackedCardCarousel(props: Props) {
  const tracking = useContext(TrackingContext)
  const hasScrolledRight = useRef<boolean>(false)
  const hasScrolledLeft = useRef<boolean>(false)
  const currentLeft = useRef<number>(0)
  const hasClickedRight = useRef<boolean>(false)
  const hasClickedLeft = useRef<boolean>(false)
  const [inViewRef] = useInView({ triggerOnce: true })

  const newTracking = useMemo<TrackingProps | undefined>(() => {
    if (tracking && !tracking.label?.includes('Carousel')) {
      return ({
        ...tracking,
        label: `${tracking.label} - Carousel`,
      })
    }
    return tracking
  }, [tracking])

  const onScroll = useMemo(() => debounce((e: React.UIEvent<HTMLDivElement>) => {
    if (newTracking) {
      const nextLeft = getLogicalScrollLeft(e.currentTarget)
      const direction = nextLeft > currentLeft.current ? 'Right' : 'Left'
      currentLeft.current = nextLeft

      if ((direction === 'Right' && hasScrolledRight.current) ||
        (direction === 'Left' && hasScrolledLeft.current)) {
        return
      }

      if (direction === 'Right') {
        hasScrolledRight.current = true
      } else {
        hasScrolledLeft.current = true
      }
    }
  }, 100), [newTracking])

  const onArrowCLick = useCallback((e: React.MouseEvent<HTMLButtonElement>, direction: ArrowDirection) => {
    const hasClicked = direction === 'left' ? hasClickedLeft.current : hasClickedRight.current
    if (newTracking && !hasClicked) {
      if (direction === 'left') {
        hasClickedLeft.current = true
      } else {
        hasClickedRight.current = true
      }
    }
  }, [newTracking])

  return (
    <TrackingContext.Provider value={newTracking}>
      <CardCarousel
        {...props}
        onArrowClick={onArrowCLick}
        onScroll={onScroll}
        ref={inViewRef}
      />
    </TrackingContext.Provider>
  )
}

export default TrackedCardCarousel
